import * as THREE from 'three';
import { BLACK, DSTEEL, DWOOD, WOOD, cloth, emis, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The sappers' fort (era II incursion): a crude earthwork behind a ring of sharpened stakes, sandbagged,
 * with a timbered tunnel mouth breathing violet dream-dust, black-powder kegs on a sledge and a tattered
 * black banner with a red claw. What it gives up is dream dust, dug out in sacks and spilled on the spoil.
 * And the mound a sapper raises as it digs under a wall.
 */

const EARTH = stone('#3b3026');
const EARTH_DK = stone('#2a221b');
const SPOIL = stone('#4a3c2e');
const SACK = cloth('#5d5242');
const SACK_DK = cloth('#4a4134');
const KEG_IRON = DSTEEL;
const FUSE = std('#1c1814', { roughness: 1 });
const BANNER = cloth('#16141a', { side: THREE.DoubleSide });
const CLAW = cloth('#8e1f17', { side: THREE.DoubleSide });
const DUST = emis('#c9b4ff', '#7b4dff', 1.4, { roughness: 0.4, flatShading: true });
const DUST_DK = emis('#7a5fc0', '#4a2aa0', 0.9, { roughness: 0.5, flatShading: true });

const box = (p: THREE.Object3D, mat: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number, ry = 0): THREE.Mesh => {
  const m = M(new THREE.BoxGeometry(w, h, d), mat, p);
  m.position.set(x, y, z);
  m.rotation.y = ry;
  return m;
};

/** A heap of earth: a flattened, lumpy dome. */
const heap = (p: THREE.Object3D, mat: THREE.Material, r: number, h: number, x: number, z: number, rng: () => number): void => {
  const m = M(new THREE.DodecahedronGeometry(r, 1), mat, p);
  m.scale.set(1, h / r, 0.9 + rng() * 0.2);
  m.position.set(x, 0.02, z);
  m.rotation.y = rng() * 3;
};

/** A sandbag: a squashed pillow of burlap. */
const sandbag = (p: THREE.Object3D, x: number, y: number, z: number, ry: number, dk: boolean): void => {
  const m = M(new THREE.SphereGeometry(0.06, 7, 5), dk ? SACK_DK : SACK, p);
  m.scale.set(1.5, 0.62, 0.95);
  m.position.set(x, y, z);
  m.rotation.y = ry;
};

/** A black-powder keg: banded staves, a fuse sticking out of the bung. */
const keg = (p: THREE.Object3D, x: number, y: number, z: number, lying = false): void => {
  const k = G(p, x, y, z);
  if (lying) k.rotation.z = Math.PI / 2;
  const body = M(new THREE.CylinderGeometry(0.06, 0.06, 0.15, 10), DWOOD, k);
  body.scale.set(1.08, 1, 1.08);
  for (const by of [-0.05, 0.05]) {
    const b = M(new THREE.CylinderGeometry(0.067, 0.067, 0.014, 10), KEG_IRON, k);
    b.position.y = by;
  }
  if (!lying) limb(k, V(0.02, 0.075, 0), V(0.05, 0.13, 0.02), 0.005, 0.004, FUSE, 3);
};

/**
 * The fort at the clearing's heart: a horseshoe of sharpened stakes round an earth bank, sandbags along
 * its lip, a timbered tunnel mouth leaking violet dust, kegs on a sledge, a pick and shovel in the spoil
 * and a tattered black banner with a red claw over it all.
 */
export const sapperFort = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 201);
  const { g } = c;
  const rng = mulberry32(2011);
  M(new THREE.CylinderGeometry(0.68, 0.76, 0.1, 9), EARTH_DK, g).position.y = 0.05;
  // The bank: heaped earth, highest at the back.
  heap(g, EARTH, 0.42, 0.34, 0, -0.26, rng);
  heap(g, EARTH_DK, 0.28, 0.22, -0.38, -0.08, rng);
  heap(g, EARTH, 0.27, 0.2, 0.4, -0.1, rng);
  // The palisade: a horseshoe of sharpened, leaning stakes, lashed by two rails.
  const n = 15;
  for (let i = 0; i < n; i++) {
    const a = Math.PI * (0.92 + (1.16 * i) / (n - 1));
    const r = 0.6 + (rng() - 0.5) * 0.04;
    const x = Math.cos(a) * r, z = Math.sin(a) * r;
    const h = 0.42 + rng() * 0.14;
    const lean = 0.08 + rng() * 0.06;
    const top = V(x * (1 + lean), h, z * (1 + lean));
    limb(g, V(x, 0.08, z), top, 0.028, 0.024, i % 3 === 0 ? DWOOD : WOOD, 5);
    const dir = top.clone().sub(V(x, 0.08, z)).normalize();
    limb(g, top, top.clone().addScaledVector(dir, 0.08), 0.024, 0.002, DWOOD, 5);
  }
  for (const y of [0.2, 0.34]) {
    const rail = M(new THREE.TorusGeometry(0.62, 0.012, 4, 18, Math.PI * 1.1), DWOOD, g);
    rail.rotation.x = Math.PI / 2;
    rail.rotation.z = -Math.PI * 0.95;
    rail.position.y = y;
  }
  // Sandbags along the front lip, two courses, either side of the tunnel.
  for (const side of [-1, 1]) {
    for (let row = 0; row < 2; row++) {
      for (let i = 0; i < 3 - row; i++) {
        const x = side * (0.2 + i * 0.13 + row * 0.06);
        sandbag(g, x, 0.13 + row * 0.07, 0.2 - i * 0.04 + row * 0.01, side * 0.4 + rng() * 0.2, (i + row) % 2 === 1);
      }
    }
  }
  // The tunnel mouth: a dark hole cut into the bank, framed by props and a lintel, violet dust in its throat.
  const tz = 0.17;
  const mouth = M(new THREE.CircleGeometry(0.13, 10), BLACK, g);
  mouth.scale.y = 1.15;
  mouth.position.set(0, 0.2, tz - 0.02);
  mouth.rotation.x = -0.25;
  box(g, BLACK, 0.22, 0.26, 0.12, 0, 0.17, tz - 0.07);
  for (const k of [-1, 1]) limb(g, V(k * 0.14, 0.08, tz), V(k * 0.12, 0.36, tz - 0.02), 0.022, 0.02, DWOOD, 6);
  box(g, DWOOD, 0.36, 0.05, 0.06, 0, 0.37, tz - 0.02, 0).rotation.z = 0.06;
  M(new THREE.DodecahedronGeometry(0.045, 0), DUST, g).position.set(-0.03, 0.12, tz - 0.03);
  M(new THREE.DodecahedronGeometry(0.03, 0), DUST_DK, g).position.set(0.04, 0.11, tz - 0.02);
  glowSprite(g, '#9b6bff', 0.5, V(0, 0.18, tz + 0.04), 0.85);
  // Dug-out spoil spilling from the mouth, with a pick and a shovel stuck in it.
  heap(g, SPOIL, 0.14, 0.07, 0.04, 0.34, rng);
  heap(g, SPOIL, 0.1, 0.05, -0.12, 0.42, rng);
  for (let i = 0; i < 4; i++) {
    const d = M(new THREE.DodecahedronGeometry(0.018 + rng() * 0.012, 0), i % 2 ? DUST : DUST_DK, g);
    d.position.set(0.02 + (rng() - 0.5) * 0.18, 0.1 + rng() * 0.03, 0.36 + (rng() - 0.5) * 0.12);
  }
  const pick = G(g, -0.1, 0.1, 0.4);
  pick.rotation.set(-0.3, 0.6, 0.4);
  limb(pick, V(0, 0, 0), V(0, 0.28, 0), 0.009, 0.009, WOOD, 4);
  const ph = M(new THREE.TorusGeometry(0.065, 0.011, 4, 8, Math.PI * 0.7), DSTEEL, pick);
  ph.position.y = 0.23;
  ph.rotation.z = Math.PI * 0.15;
  const shovel = G(g, 0.12, 0.08, 0.38);
  shovel.rotation.set(0.25, -0.3, -0.35);
  limb(shovel, V(0, 0.05, 0), V(0, 0.32, 0), 0.008, 0.008, WOOD, 4);
  box(shovel, DSTEEL, 0.07, 0.09, 0.012, 0, 0.02, 0);
  // Kegs on a sledge, to the right; one lying in the dirt.
  const sl = G(g, 0.36, 0.1, 0.24);
  sl.rotation.y = -0.5;
  for (const k of [-1, 1]) box(sl, DWOOD, 0.03, 0.025, 0.3, k * 0.08, 0, 0);
  box(sl, WOOD, 0.22, 0.015, 0.26, 0, 0.02, 0);
  keg(sl, -0.05, 0.1, -0.05);
  keg(sl, 0.06, 0.1, 0.05);
  keg(sl, -0.04, 0.1, 0.07);
  keg(g, 0.52, 0.16, 0.06, true);
  // The banner on a tall crooked pole at the back: black, torn at the hem, a red claw on it.
  const bx = -0.18, bz = -0.36;
  limb(g, V(bx, 0.2, bz), V(bx + 0.02, 1.12, bz), 0.016, 0.012, DWOOD, 5);
  box(g, DWOOD, 0.3, 0.018, 0.018, bx + 0.15, 1.08, bz);
  const flag = G(g, bx + 0.02, 1.07, bz + 0.01);
  const cloth0 = new THREE.PlaneGeometry(0.28, 0.4, 4, 5);
  const pos = cloth0.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i);
    const torn = y < -0.15 ? (Math.sin(x * 60) > 0.2 ? -0.06 : 0.02) : 0;
    pos.setXYZ(i, x + 0.14, y - 0.2 + torn, Math.sin(x * 14 + y * 6) * 0.025);
  }
  cloth0.computeVertexNormals();
  M(cloth0, BANNER, flag);
  // A red claw: three raked slashes.
  for (let i = 0; i < 3; i++) {
    const s = M(new THREE.PlaneGeometry(0.03, 0.17), CLAW, flag);
    s.position.set(0.09 + i * 0.05, -0.2, 0.034);
    s.rotation.z = -0.35;
  }
  // A torch on a stake by the mouth: the one warm light.
  limb(g, V(-0.3, 0.1, 0.16), V(-0.3, 0.46, 0.14), 0.01, 0.01, DWOOD, 4);
  M(new THREE.SphereGeometry(0.025, 6, 4), emis('#ffc890', '#ff6a1a', 1.8), g).position.set(-0.3, 0.49, 0.14);
  glowSprite(g, '#ff7a2a', 0.3, V(-0.3, 0.5, 0.15), 0.8);
  return done(c, 1.15);
};

/** What the fort gives up: a spoil heap glittering with violet dream dust, a split sack and a keg of it. */
export const dustSpoil = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 202);
  const { g } = c;
  const rng = mulberry32(2021);
  heap(g, SPOIL, 0.3, 0.22, 0, 0, rng);
  heap(g, EARTH, 0.18, 0.12, 0.18, 0.12, rng);
  // Crystals of dream dust breaking through the spoil.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + rng();
    const d = 0.06 + rng() * 0.18;
    const k = M(new THREE.OctahedronGeometry(0.03 + rng() * 0.025, 0), i % 3 === 0 ? DUST_DK : DUST, g);
    k.scale.y = 1.6 + rng();
    k.position.set(Math.cos(a) * d, 0.12 + (0.24 - d) * 0.5, Math.sin(a) * d);
    k.rotation.set((rng() - 0.5) * 0.8, rng() * 3, (rng() - 0.5) * 0.8);
  }
  // A split sack spilling glowing dust, and a keg of it.
  const sk = M(new THREE.SphereGeometry(0.08, 8, 6), SACK, g);
  sk.scale.set(1, 1.2, 0.9);
  sk.position.set(-0.26, 0.09, 0.14);
  const spill = M(new THREE.CircleGeometry(0.1, 9), DUST, g);
  spill.rotation.x = -Math.PI / 2;
  spill.scale.set(1.4, 0.8, 1);
  spill.position.set(-0.18, 0.012, 0.24);
  keg(g, 0.3, 0.08, -0.12);
  glowSprite(g, '#9b6bff', 0.4, V(0, 0.28, 0), 0.45);
  return done(c, 0.45);
};

const FRESH = stone('#6e543a');

/** The hole a sapper goes down: a dark pit in a ring of fresh-dug earth, clods thrown round it. */
export const dirtMound = (o?: BaseOpts): Model => {
  const c = begin(o, 0.4, 203);
  const { g } = c;
  const rng = mulberry32(2031);
  const n = 9;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rng() * 0.3;
    const d = 0.15 + rng() * 0.03;
    const m = M(new THREE.DodecahedronGeometry(0.075 + rng() * 0.035, 0), i % 3 === 0 ? EARTH : i % 3 === 1 ? FRESH : SPOIL, g);
    m.scale.y = 0.55 + rng() * 0.3;
    m.position.set(Math.cos(a) * d, 0.03, Math.sin(a) * d);
    m.rotation.set(rng(), rng() * 3, rng());
  }
  const hole = M(new THREE.CircleGeometry(0.12, 10), BLACK, g);
  hole.rotation.x = -Math.PI / 2;
  hole.position.y = 0.035;
  const rim = M(new THREE.CircleGeometry(0.2, 12), EARTH_DK, g);
  rim.rotation.x = -Math.PI / 2;
  rim.position.y = 0.012;
  for (let i = 0; i < 10; i++) {
    const a = rng() * Math.PI * 2;
    const d = 0.26 + rng() * 0.12;
    const k = M(new THREE.DodecahedronGeometry(0.02 + rng() * 0.02, 0), i % 2 ? FRESH : EARTH_DK, g);
    k.position.set(Math.cos(a) * d, 0.015, Math.sin(a) * d);
    k.rotation.set(rng() * 3, rng() * 3, 0);
  }
  return done(c, 0.12);
};

/** The ground heaved up over a tunnel: a low, split ridge of earth along +z. */
export const dirtRidge = (o?: BaseOpts): Model => {
  const c = begin(o, 0.3, 204);
  const { g } = c;
  const rng = mulberry32(2041);
  for (let i = 0; i < 4; i++) {
    const m = M(new THREE.DodecahedronGeometry(0.07 + rng() * 0.02, 0), i % 2 ? FRESH : EARTH, g);
    m.scale.set(1.1, 0.5, 1.3);
    m.position.set((rng() - 0.5) * 0.04, 0.015, -0.15 + i * 0.1);
    m.rotation.y = rng() * 3;
  }
  const cr = box(g, BLACK, 0.025, 0.02, 0.3, 0, 0.05, 0, 0.08);
  cr.rotation.x = 0.05;
  return done(c, 0.08);
};
